/**
 * listingBus.js — cross-tab + in-page revalidation for listing edits.
 *
 * The EditListingModal publishes the merged listing returned by
 * `PATCH /api/listings/{id}` here. Every surface that renders listings
 * (Browse, ListingDetail, ParentLedger, KidProfile) subscribes and
 * splices the updated listing into its local state so badges refresh
 * instantly — no reload, no polling, and it works across tabs via the
 * `BroadcastChannel` API.
 *
 * `storage` events are used as a fallback for browsers without
 * BroadcastChannel (older Safari). Both mechanisms are idempotent — the
 * subscriber just receives the updated listing payload.
 */

const CHANNEL = "listing:updates";
let bc = null;
try {
  if (typeof BroadcastChannel !== "undefined") {
    bc = new BroadcastChannel(CHANNEL);
  }
} catch { /* older browsers — fallback to storage events */ }

const localSubs = new Set();

export function publishListingUpdate(listing) {
  if (!listing || !listing.id) return;
  const payload = { ts: Date.now(), listing };
  // Fan out to in-page subscribers immediately.
  localSubs.forEach((cb) => {
    try { cb(listing); } catch { /* isolate subscriber errors */ }
  });
  // Fan out to other tabs.
  try { bc?.postMessage(payload); } catch { /* channel closed */ }
  try {
    // localStorage tick doubles as a cross-tab poke when BroadcastChannel
    // isn't available.
    window.localStorage.setItem(CHANNEL, JSON.stringify(payload));
  } catch { /* private mode / quota */ }
}

export function subscribeListingUpdates(cb) {
  if (typeof cb !== "function") return () => {};
  localSubs.add(cb);

  const onBC = (e) => {
    if (e?.data?.listing) cb(e.data.listing);
  };
  const onStorage = (e) => {
    if (e.key !== CHANNEL || !e.newValue) return;
    try {
      const parsed = JSON.parse(e.newValue);
      if (parsed?.listing) cb(parsed.listing);
    } catch { /* ignore */ }
  };

  bc?.addEventListener?.("message", onBC);
  window.addEventListener("storage", onStorage);

  return () => {
    localSubs.delete(cb);
    bc?.removeEventListener?.("message", onBC);
    window.removeEventListener("storage", onStorage);
  };
}

/**
 * mergeListingInto — helper for surfaces that keep a list in state.
 * Returns a new array with the updated listing spliced in when its id
 * matches, otherwise returns the input array unchanged (referential
 * equality preserved so React skips renders).
 */
export function mergeListingInto(list, updated) {
  if (!Array.isArray(list) || !updated?.id) return list;
  let hit = false;
  const next = list.map((l) => {
    if (l && l.id === updated.id) { hit = true; return { ...l, ...updated }; }
    return l;
  });
  return hit ? next : list;
}
